import { useState } from 'react';

/**
 * A panel width (px) that survives reload, kept in `localStorage` — a per-device
 * layout preference, so no server round-trip. `undefined` until the user has
 * resized once. Returns `[width, setWidth, persist]`: `setWidth` is the live
 * value (every drag step), `persist` writes storage — call it once when the drag
 * ends, not per step. Storage can be blocked (private mode, disabled site data), so
 * every access is guarded and the width just falls back to session-only.
 */
export function usePersistedWidth(
  key: string,
): [number | undefined, (width: number) => void, (width: number) => void] {
  const [width, setWidth] = useState<number | undefined>(() => {
    try {
      const n = Number(window.localStorage.getItem(key));
      return Number.isFinite(n) && n > 0 ? n : undefined;
    } catch {
      return undefined;
    }
  });
  function persist(next: number) {
    try {
      window.localStorage.setItem(key, String(next));
    } catch {
      // Not persisted — the in-memory width still applies for this session.
    }
  }
  return [width, setWidth, persist];
}
